import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bot, Send, Sparkles, User, Network } from "lucide-react";
import { API } from "../../lib/api";
import { nextMid } from "../../lib/ids";
import { QUICK_PROMPTS } from "../../lib/constants";
import { Panel, Overline } from "../ui";
import { ArtifactCluster } from "../ArtifactCluster";
import { ReportExportPanel } from "../ReportExportPanel";
import { CopilotActions } from "../CopilotActions";
import { useI18n } from "../../lib/i18n";

const bucket = (cls = "") => {
  const c = String(cls).toUpperCase();
  if (c.includes("MALICIOUS")) return "CONFIRMED_MALICIOUS";
  if (
    c.includes("SUSPICIOUS") ||
    c.includes("VIOLATION") ||
    c.includes("ATTEMPT")
  )
    return "SUSPICIOUS";
  if (c.includes("BENIGN")) return "BENIGN";
  return "INDETERMINATE";
};

export default function AiWorkspace({
  messages,
  setMessages,
  sessionRef,
  busy,
  setBusy,
  evidence,
  blocks,
  report,
  summary,
  matter,
  officer,
  clientHash,
  onEnsureReport,
  onHandoff,
}) {
  const { t, lang } = useI18n();
  const [input, setInput] = useState("");
  const endRef = useRef(null);

  const nodes = useMemo(() => {
    const ev = (evidence || []).map((e) => ({
      id: e.evidence_id,
      label: e.evidence_id,
      group: bucket(e.classification),
      score: e.threat_score || 10,
      meta: `${e.filename} · ${e.sealed ? "sealed" : "unsealed"}`,
    }));
    const bl = (blocks || []).slice(0, 22).map((b) => ({
      id: `b-${b.index}`,
      label: `#${b.index} ${b.action_type}`,
      group: "CUSTODY",
      score: 14,
      meta: b.hash?.slice(0, 18),
    }));
    return [...ev, ...bl];
  }, [evidence, blocks]);

  const clusters = useMemo(
    () => new Set(nodes.map((n) => n.group)).size,
    [nodes],
  );

  const lastUserPrompt = useMemo(
    () => [...messages].reverse().find((m) => m.role === "user")?.content || "",
    [messages],
  );

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [
      ...m,
      { mid: nextMid(), role: "user", content: msg },
      { mid: nextMid(), role: "assistant", content: "" },
    ]);

    const setLast = (content) =>
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        copy[copy.length - 1] = { ...last, role: "assistant", content };
        return copy;
      });

    try {
      const res = await fetch(`${API}/copilot/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current }),
      });
      if (!res.ok || !res.body) {
        setLast(`[Aegis plane error ${res.status}]`);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let data;
          try {
            data = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (data.type === "session") sessionRef.current = data.session_id;
          else if (data.type === "delta" || data.type === "error") {
            acc += data.content;
            setLast(acc);
          }
        }
      }
    } catch {
      setLast("[connection lost] Aegis inference plane unreachable.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="screen-ai">
      <div>
        <Overline>{t("ai.overline")}</Overline>
        <h1 className="mt-1.5 font-heading text-3xl font-light tracking-tighter text-slate-100">
          {t("ai.title")}
        </h1>
        <p className="mt-2 text-sm text-slate-400">{t("ai.sub")}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="xl:col-span-8">
          <Panel
            icon={Network}
            title={t("ai.cluster")}
            subtitle={t("ai.cluster.sub")}
            accent="cyan"
            testid="cluster-panel"
            actions={
              <span className="font-mono text-[11px] text-slate-400">
                {nodes.length} {t("ai.nodes")} · {clusters} {t("ai.clusters")}
              </span>
            }
          >
            {nodes.length === 0 ? (
              <p className="py-16 text-center text-sm text-slate-400">
                {t("ai.cluster.empty")}
              </p>
            ) : (
              <ArtifactCluster nodes={nodes} height={380} />
            )}
          </Panel>
        </div>
        <div className="xl:col-span-4">
          <ReportExportPanel
            report={report}
            summary={summary}
            evidence={evidence}
            matter={matter}
            officer={officer}
            clientHash={clientHash}
            onEnsureReport={onEnsureReport}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="xl:col-span-7">
          <div className="rounded-xl border border-aegis-purple/25 bg-panel/70">
            <div className="flex items-center gap-2 border-b border-edge px-5 py-3">
              <span className="grid place-items-center h-8 w-8 rounded-lg border border-aegis-purple/40 bg-aegis-purple/10 text-aegis-purple">
                <Bot size={16} />
              </span>
              <div>
                <p className="text-sm font-medium text-slate-100">{t("ai.chat")}</p>
                <p className="font-mono text-[10px] text-aegis-emerald">
                  ● {t("ai.status")}
                </p>
              </div>
            </div>

            <div
              className="h-[44vh] min-h-[300px] space-y-4 overflow-y-auto p-5"
              data-testid="copilot-messages"
            >
              {messages.map((m, i) => (
                <div
                  key={m.mid ?? `m${i}`}
                  className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  <span
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border ${
                      m.role === "user"
                        ? "border-aegis-cyan/40 bg-aegis-cyan/10 text-aegis-cyan"
                        : "border-aegis-purple/40 bg-aegis-purple/10 text-aegis-purple"
                    }`}
                  >
                    {m.role === "user" ? <User size={15} /> : <Bot size={15} />}
                  </span>
                  <div
                    className={`max-w-[78%] whitespace-pre-wrap rounded-xl border px-4 py-3 text-[13px] leading-relaxed ${
                      m.role === "user"
                        ? "border-aegis-cyan/20 bg-aegis-cyan/5 text-slate-100"
                        : "border-aegis-purple/20 bg-aegis-purple/[0.06] text-slate-200"
                    }`}
                  >
                    {m.content ||
                      (busy && i === messages.length - 1 ? (
                        <span className="animate-pulseGlow">▍</span>
                      ) : (
                        ""
                      ))}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div className="border-t border-edge px-5 py-3">
              <div className="mb-3 flex flex-wrap gap-2">
                {QUICK_PROMPTS[lang].map((p, i) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    disabled={busy}
                    data-testid={`quick-prompt-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-edge raise px-3 py-1 text-[11px] text-slate-400 transition-colors hover:border-aegis-purple/40 hover:text-aegis-purple disabled:opacity-40"
                  >
                    <Sparkles size={11} /> {p}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t("ai.placeholder")}
                  data-testid="copilot-input"
                  className="flex-1 rounded-lg border border-edge well px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-500"
                />
                <button
                  onClick={() => send()}
                  disabled={busy || !input.trim()}
                  data-testid="copilot-send-btn"
                  className="grid h-10 w-10 place-items-center rounded-lg border border-aegis-purple/40 bg-aegis-purple/15 text-aegis-purple transition-colors hover:bg-aegis-purple/25 disabled:opacity-40"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="xl:col-span-5">
          <CopilotActions seedIntent={lastUserPrompt} onHandoff={onHandoff} />
        </div>
      </div>
    </div>
  );
}
